import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaFacebook, FaLinkedinIn, FaPhone, FaEnvelope, FaMapMarkerAlt, FaYoutube } from 'react-icons/fa';
import '../assets/css/Footer.css';
import footerLogo from '../assets/images/footer-logo.png';

const Footer = () => {
  // Add responsive state management
  const [isMobile, setIsMobile] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setIsMobile(width <= 767);
  }, []);

  useEffect(() => {
    handleResize();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize]);

  const handleOrientationChange = () => {
    setTimeout(handleResize, 100);
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  // Handle link click with scroll to top
  const handleLinkClick = (e) => {
    // Don't call handleTouchEnd here as it can cause issues
    setTimeout(scrollToTop, 100); // Small delay to ensure navigation starts first
  };

  // Handle image loading
  const handleImageLoad = () => {
    // setImageLoaded(true); // This state variable was removed
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    try {
      const target = e?.currentTarget;
      target?.classList?.add('touching');
    } catch (error) {
      // Silently handle any classList errors
    }
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    try {
      const target = e?.currentTarget;
      if (target?.classList) {
        setTimeout(() => {
          target?.classList?.remove('touching');
        }, 100);
      }
    } catch (error) {
      // Silently handle any classList errors
    }
  };

  // Social media links with responsive behavior
  const socialLinks = [
    { icon: FaInstagram, url: 'https://www.instagram.com/designcraft.ae/', target: '_blank', name: 'Instagram', class: 'instagram' },
    { icon: FaFacebook, url: 'https://www.facebook.com/profile.php?id=61551538633956&sk', target: '_blank', name: 'Facebook', class: 'facebook' },
    { icon: FaYoutube, url: 'https://www.youtube.com/@DesignCraft-te4hm', target: '_blank', name: 'Youtube', class: 'youtube' },
    { icon: FaLinkedinIn, url: 'https://www.linkedin.com/company/101256669', target: '_blank', name: 'LinkedIn', class: 'linkedin' }
   
  ];

  // Quick links data
  const quickLinks = [
    { to: '/about', label: 'About Us' },
    { to: '/blog', label: 'Blog' },
    { to: '/design-ideas', label: 'Design Ideas' },
    { to: '/design-fitout', label: 'Fitout' },
    { to: '/gallery', label: 'Gallery' }
   
  ];

  // Customer service links
  const customerServiceLinks = [
    { to: '/contact', label: 'Contact' },
    { to: '/faq', label: 'FAQ' },
    { to: '/terms', label: 'Terms & Condition' },
    { to: '/privacy', label: 'Privacy Policy' }
  ];

  // Contact information
  const contactInfo = {
    emails: ['info@designcraft.ae', 'sales@designcraft.ae'],
    phones: ['04 337 34 46', '+971 50 1900 982'],
    address: {
      line1: 'Abdul Razzaq Mohiddin Abdulla',
      line2: 'Warehouse Shed No-1 Opp. Pedal Art',
      line3: 'Al Quoz Industrial Area – 1, Dubai'
    }
  };

  return (
    <footer className="footer">
      <div className="footer-content">
        {/* Logo and Description Section */}
        <div className="footer-section">
          {!imageError ? (
            <img 
              src={footerLogo} 
              alt="Design Craft Logo" 
              className="footer-logo"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : (
            <div className="footer-logo-fallback">
              DesignCraft
            </div>
          )}
          
          <p>
            {isMobile 
              ? 'DesignCraft Company - crafting excellence in furniture design with passion and style.'
              : 'At DesignCraft Company, our story is one of passion, craftsmanship, and a relentless pursuit of excellence. It all began with a vision to redefine the way people experience furniture – not just as functional pieces, but as expressions of personal style and comfort.'
            }
          </p>
          
          <div className="social-icons">
            {socialLinks.map((social, index) => {
              const IconComponent = social.icon;
              return (
                <a 
                  key={index}
                  href={social.url} 
                  className={`social-icon ${social.class}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  title={social.name}
                >
                  <IconComponent />
                </a>
              );
            })}
          </div>
        </div>

        {/* Quick Links Section */}
        <div className="footer-section">
          <h3>Quick Links</h3>
          <ul className="footer-links">
            {quickLinks.map((link, index) => (
              <li key={index}>
                <Link 
                  to={link.to}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onClick={handleLinkClick}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Customer Service Section */}
        <div className="footer-section">
          <h3>Customer Service</h3>
          <ul className="footer-links">
            {customerServiceLinks.map((link, index) => (
              <li key={index}>
                <Link 
                  to={link.to}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                  onClick={handleLinkClick}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Information Section */}
        <div className="footer-section">
          <h3>Contact Info</h3>
          
          <div className="contact-info">
            {/* Email Items */}
            {contactInfo.emails.map((email, index) => (
              <div key={`email-${index}`} className="contact-item">
                <FaEnvelope className="contact-icon" />
                <a href={`mailto:${email}`}>
                  {email}
                </a>
              </div>
            ))}

            {/* Phone Items */}
            {contactInfo.phones.map((phone, index) => (
              <div key={`phone-${index}`} className="contact-item">
                <FaPhone className="contact-icon" />
                <a href={`tel:${phone.replace(/\s/g, '')}`}>
                  {phone}
                </a>
              </div>
            ))}

            {/* Address */}
            <div className="contact-item">
              <FaMapMarkerAlt className="contact-icon" />
              <div>
                {contactInfo.address.line1}<br />
                {contactInfo.address.line2}<br />
                {contactInfo.address.line3}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>
          © 2025-2026 Designcraft. All Rights Reserved |  
          <Link to="designcraft.ae" onClick={handleLinkClick}>
            DesignCraft
          </Link>
        </p>
      </div>
    </footer>
  );
};

export default Footer; 